import { PORTFOLIO_BENCHMARK_CACHE_TTL_MS } from '@/constants/common.constant'
import { raDashboardService } from '@/services/report-dashboard/raDashboard.main.service'
import {
  getBenchmarkList,
  getPortfolioList,
} from '@/services/scenarioBuilder.service'
import {
  setBenchmarkList,
  setBenchmarkListLoading,
  setPortfolioList,
  setPortfolioListLoading,
} from '@/states/scenarioBuilder.slice'
import type { AppDispatch } from '@/store'
import { useEffect } from 'react'
import { useAppDispatch } from './use-app-dispatch'
import { useAppSelector } from './use-app-selector'

const isStale = (fetchedAt: number | null): boolean =>
  !fetchedAt || Date.now() - fetchedAt > PORTFOLIO_BENCHMARK_CACHE_TTL_MS

const fetchPortfolioList = async (dispatch: AppDispatch): Promise<boolean> => {
  try {
    dispatch(setPortfolioListLoading(true))
    const response = await getPortfolioList()
    dispatch(setPortfolioList(response))
    return true
  } catch (error) {
    console.error('There was a problem with the fetch operation:', error)
    return false
  } finally {
    dispatch(setPortfolioListLoading(false))
  }
}

const fetchBenchmarkListData = async (dispatch: AppDispatch): Promise<boolean> => {
  try {
    dispatch(setBenchmarkListLoading(true))
    const response = await getBenchmarkList()
    dispatch(setBenchmarkList(response))
    return true
  } catch (error) {
    console.error('There was a problem with the fetch operation:', error)
    return false
  } finally {
    dispatch(setBenchmarkListLoading(false))
  }
}

/**
 * Force-refetches both lists regardless of cache freshness — used by the
 * manual "Refresh Portfolio and Benchmark Data" action in RunScenarioModal.
 * Resolves to false if either fetch failed (the stale list is left in place
 * either way), so the caller can surface the failure to the user.
 *
 * Also invalidates the separate, non-persisted RA Dashboard portfolio cache
 * (RADashboardService.cachedData) — it shares the same TTL story but isn't
 * backed by this Redux slice, so it wouldn't otherwise be touched by this
 * refresh action even though the tooltip promises "Portfolio and Benchmark
 * Data" broadly. RADashboardPage itself may not be mounted when this runs;
 * clearing just ensures its next getPortfolioData() call refetches instead
 * of serving stale data for up to PORTFOLIO_BENCHMARK_CACHE_TTL_MS more.
 */
export const refreshPortfolioAndBenchmarkLists = async (
  dispatch: AppDispatch,
): Promise<boolean> => {
  raDashboardService.clearCache()
  const [portfolioSucceeded, benchmarkSucceeded] = await Promise.all([
    fetchPortfolioList(dispatch),
    fetchBenchmarkListData(dispatch),
  ])
  return portfolioSucceeded && benchmarkSucceeded
}

/**
 * Ensures the shared scenarioBuilder portfolio/benchmark lists are loaded and
 * not stale, fetching only what's missing or older than
 * PORTFOLIO_BENCHMARK_CACHE_TTL_MS. Both lists are persisted via redux-persist,
 * so this also covers freshness across full page reloads, not just remounts.
 *
 * Shared by ScenarioBuilderPage and ReportSchedulesPage so the fetch/cache
 * logic exists in one place instead of being duplicated per page.
 */
export const usePortfolioAndBenchmarkLists = (shouldLoad: boolean): void => {
  const dispatch = useAppDispatch()
  const portfolioList = useAppSelector((s) => s.scenarioBuilder.portfolioList)
  const benchmarkList = useAppSelector((s) => s.scenarioBuilder.benchmarkList)
  const portfolioListFetchedAt = useAppSelector(
    (s) => s.scenarioBuilder.portfolioListFetchedAt,
  )
  const benchmarkListFetchedAt = useAppSelector(
    (s) => s.scenarioBuilder.benchmarkListFetchedAt,
  )

  useEffect(() => {
    if (!shouldLoad) return

    if (portfolioList.length === 0 || isStale(portfolioListFetchedAt)) {
      fetchPortfolioList(dispatch)
    }

    if (benchmarkList.length === 0 || isStale(benchmarkListFetchedAt)) {
      fetchBenchmarkListData(dispatch)
    }
    // Only re-evaluate when `shouldLoad` flips true — re-running on every
    // list/timestamp change would refire the instant a fetch completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad])
}
